
import { debug } from './debug';
/**
 * 性能优化相关工具
 */
export class PerformanceCache<K, V> {
    private cache = new Map<K, V>();
    private accessTimes = new Map<K, number>();
    private maxSize: number;

    constructor(maxSize: number = 100) {
        this.maxSize = maxSize;
    }

    get(key: K): V | undefined {
        const value = this.cache.get(key);
        if (value !== undefined) {
            this.accessTimes.set(key, Date.now());
        }
        return value;
    }

    set(key: K, value: V): void {
        if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
            this.evictLRU();
        }
        this.cache.set(key, value);
        this.accessTimes.set(key, Date.now());
    }

    has(key: K): boolean {
        return this.cache.has(key);
    }

    delete(key: K): boolean {
        this.accessTimes.delete(key);
        return this.cache.delete(key);
    }

    clear(): void {
        this.cache.clear();
        this.accessTimes.clear();
    }

    size(): number {
        return this.cache.size;
    }

    private evictLRU(): void {
        let oldestKey: K | undefined;
        let oldestTime = Number.MAX_SAFE_INTEGER;

        for (const [key, time] of this.accessTimes) {
            if (time < oldestTime) {
                oldestTime = time;
                oldestKey = key;
            }
        }

        if (oldestKey !== undefined) {
            this.delete(oldestKey);
        }
    }
}

export class PerformanceTracker {
    private timers = new Map<string, number>();
    private counters = new Map<string, number>();

    startTimer(name: string): void {
        this.timers.set(name, Date.now());
    }

    endTimer(name: string): number {
        const start = this.timers.get(name);
        if (start) {
            const duration = Date.now() - start;
            this.timers.delete(name);
            return duration;
        }
        return 0;
    }

    increment(name: string): void {
        this.counters.set(name, (this.counters.get(name) || 0) + 1);
    }

    getCounter(name: string): number {
        return this.counters.get(name) || 0;
    }

    reset(): void {
        this.timers.clear();
        this.counters.clear();
    }

    getStats(): Record<string, any> {
        return {
            counters: Object.fromEntries(this.counters),
            activeTimers: this.timers.size
        };
    }
}

/**
 * 批处理器 - 用于批量处理数据，避免一次性处理过多数据造成阻塞
 */
export class BatchProcessor {
    private batchSize: number;
    private delay: number;

    constructor(batchSize: number = 10, delay: number = 50) {
        this.batchSize = Math.max(1, batchSize);
        this.delay = Math.max(0, delay);
    }

    /**
     * 批量处理数据
     * @param items 待处理的数据数组
     * @param processor 处理函数
     * @returns 处理结果数组
     */
    async processBatch<T, R>(
        items: T[],
        processor: (item: T) => Promise<R>
    ): Promise<R[]> {
        if (!items.length) return [];

        const results: R[] = [];

        for (let i = 0; i < items.length; i += this.batchSize) {
            const batch = items.slice(i, i + this.batchSize);
            const batchResults = await Promise.all(
                batch.map(item => processor(item))
            );
            results.push(...batchResults);

            // 添加延迟防止阻塞事件循环
            if (i + this.batchSize < items.length && this.delay > 0) {
                await new Promise(resolve => setTimeout(resolve, this.delay));
            }
        }

        return results;
    }

    /**
     * 批量处理数据（同步版本）
     * @param items 待处理的数据数组
     * @param processor 处理函数
     * @param onProgress 进度回调
     * @returns 处理结果数组
     */
    processBatchSync<T, R>(
        items: T[],
        processor: (item: T) => R,
        onProgress?: (progress: number) => void
    ): R[] {
        if (!items.length) return [];

        const results: R[] = [];

        for (let i = 0; i < items.length; i += this.batchSize) {
            const batch = items.slice(i, i + this.batchSize);
            const batchResults = batch.map(processor);
            results.push(...batchResults);

            // 报告进度
            if (onProgress) {
                const progress = Math.min(1, (i + this.batchSize) / items.length);
                onProgress(progress);
            }
        }

        return results;
    }
}

/**
 * 防抖器 - 用于防抖操作，避免频繁触发
 */
export class Debouncer {
    private timers = new Map<string, number>();

    /**
     * 防抖执行函数
     * @param key 唯一标识符
     * @param fn 要执行的函数
     * @param delay 延迟时间（毫秒）
     */
    debounce(key: string, fn: () => void, delay: number = 300): void {
        this.cancel(key);

        const timer = setTimeout(() => {
            try {
                fn();
            } catch (error) {
                // Debounced function execution error handled
            } finally {
                this.timers.delete(key);
            }
        }, delay);

        this.timers.set(key, timer as any);
    }

    /**
     * 取消指定的防抖
     * @param key 唯一标识符
     */
    cancel(key: string): void {
        const timer = this.timers.get(key);
        if (timer) {
            clearTimeout(timer);
            this.timers.delete(key);
        }
    }

    /**
     * 取消所有防抖
     */
    cancelAll(): void {
        for (const timer of this.timers.values()) {
            clearTimeout(timer);
        }
        this.timers.clear();
    }

    /**
     * 获取当前活跃的防抖数量
     */
    size(): number {
        return this.timers.size;
    }
}

/**
 * 内存优化器 - 用于管理内存清理任务
 */
export class MemoryOptimizer {
    private static instance: MemoryOptimizer;
    private cleanupTasks: Array<{
        id: string;
        task: () => void;
        priority: number;
    }> = [];
    private intervalId: number | null = null;

    private constructor() { }

    static getInstance(): MemoryOptimizer {
        if (!MemoryOptimizer.instance) {
            MemoryOptimizer.instance = new MemoryOptimizer();
        }
        return MemoryOptimizer.instance;
    }

    /**
     * 添加清理任务
     * @param id 任务ID
     * @param task 清理任务函数
     * @param priority 优先级（数字越大优先级越高）
     */
    addCleanupTask(id: string, task: () => void, priority: number = 0): void {
        // 移除已存在的相同ID任务
        this.removeCleanupTask(id);

        this.cleanupTasks.push({ id, task, priority });

        // 按优先级排序
        this.cleanupTasks.sort((a, b) => b.priority - a.priority);
    }

    /**
     * 移除清理任务
     * @param id 任务ID
     */
    removeCleanupTask(id: string): void {
        this.cleanupTasks = this.cleanupTasks.filter(t => t.id !== id);
    }

    /**
     * 开始周期性清理
     * @param intervalMs 清理间隔（毫秒）
     */
    startPeriodicCleanup(intervalMs: number = 60000): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }

        this.intervalId = setInterval(() => {
            this.runCleanup();
        }, intervalMs) as any;
    }

    /**
     * 停止周期性清理
     */
    stopPeriodicCleanup(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * 运行所有清理任务
     */
    runCleanup(): void {
        for (const { task } of this.cleanupTasks) {
            try {
                task();
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    debug.warn('Memory cleanup task failed:', error);
                }
            }
        }

        if (process.env.NODE_ENV === 'development') {
            debug.log('Memory cleanup completed', {
                tasksExecuted: this.cleanupTasks.length
            });
        }
    }

    /**
     * 获取清理任务信息
     */
    getCleanupInfo(): {
        taskCount: number;
        isRunning: boolean;
        tasks: Array<{ id: string; priority: number }>;
    } {
        return {
            taskCount: this.cleanupTasks.length,
            isRunning: this.intervalId !== null,
            tasks: this.cleanupTasks.map(({ id, priority }) => ({ id, priority }))
        };
    }

    /**
     * 释放资源
     */
    dispose(): void {
        this.stopPeriodicCleanup();
        this.runCleanup();
        this.cleanupTasks = [];
    }
}

/**
 * 异步性能测量工具
 * @param name 性能测量名称
 * @param fn 要测量的异步函数
 * @returns 包含执行结果和性能信息的对象
 */
export async function measureAsync<T>(
    name: string,
    fn: () => Promise<T>
): Promise<T & { _performance: { name: string; duration: number; timestamp: number } }> {
    const startTime = performance.now ? performance.now() : Date.now();
    const timestamp = Date.now();

    try {
        const result = await fn();
        const duration = (performance.now ? performance.now() : Date.now()) - startTime;

        // 为原始类型创建包装对象，为对象类型直接附加属性
        if (typeof result === 'object' && result !== null) {
            return Object.assign(result as any, {
                _performance: {
                    name,
                    duration,
                    timestamp
                }
            });
        } else {
            // 对于原始类型，创建一个新对象
            return Object.assign(Object(result), {
                _performance: {
                    name,
                    duration,
                    timestamp
                }
            }) as any;
        }
    } catch (error) {
        const duration = (performance.now ? performance.now() : Date.now()) - startTime;

        // 在错误信息中也包含性能信息
        if (error instanceof Error) {
            (error as any)._performance = {
                name,
                duration,
                timestamp
            };
        }

        throw error;
    }
}

/**
 * 创建一个带有性能监控的函数包装器
 * @param name 性能测量名称
 * @param fn 要包装的函数
 * @returns 包装后的函数
 */
export function withPerformanceMonitoring<Args extends any[], Return>(
    name: string,
    fn: (...args: Args) => Return
): (...args: Args) => Return {
    return (...args: Args): Return => {
        try {
            const result = fn(...args);
            globalPerformanceTracker.increment(`${name}_success`);

            // Performance monitoring completed

            return result;
        } catch (error) {
            globalPerformanceTracker.increment(`${name}_error`);

            // Performance error handled

            throw error;
        }
    };
}

// 全局实例
export const globalPerformanceTracker = new PerformanceTracker();
export const globalMemoryOptimizer = MemoryOptimizer.getInstance();
export const globalBatchProcessor = new BatchProcessor();
export const globalDebouncer = new Debouncer();